import React from 'react'
import { AlertTriangle, RefreshCw, X } from 'lucide-react'
import { logger } from '../../utils/logger'

/**
 * Standardized error display components for consistent error handling across the app.
 */

export interface ErrorDisplayProps {
  /** The error to display */
  error: Error | string | null
  /** Title for the error display */
  title?: string
  /** Whether to show error details (stack trace, etc.) */
  showDetails?: boolean
  /** Callback when retry button is clicked */
  onRetry?: () => void
  /** Callback when dismiss button is clicked */
  onDismiss?: () => void
  /** Size variant */
  size?: 'sm' | 'md' | 'lg'
  /** Whether to show as an inline component or full card */
  inline?: boolean
  /** Custom CSS class */
  className?: string
}

/**
 * ErrorDisplay component for consistent error UI
 *
 * @example
 * ```tsx
 * // Basic usage
 * <ErrorDisplay error={error} onRetry={refetch} />
 *
 * // With custom title and details
 * <ErrorDisplay
 *   error={error}
 *   title="Failed to load projects"
 *   showDetails={process.env.NODE_ENV === 'development'}
 *   onRetry={handleRetry}
 *   onDismiss={handleDismiss}
 * />
 * ```
 */
export const ErrorDisplay = React.memo<ErrorDisplayProps>(
  ({
    error,
    title,
    showDetails = false,
    onRetry,
    onDismiss,
    size = 'md',
    inline = false,
    className = '',
  }) => {
    if (!error) return null

    const errorObj = typeof error === 'string' ? new Error(error) : error
    const errorMessage = errorObj.message || 'An unexpected error occurred'

    // Log the error for debugging
    logger.error('Error displayed to user', errorObj)

    const containerClasses = [
      'error-display',
      `error-display--${size}`,
      inline && 'error-display--inline',
      className,
    ]
      .filter(Boolean)
      .join(' ')

    let iconSize = 20
    if (size === 'sm') {
      iconSize = 16
    } else if (size === 'lg') {
      iconSize = 24
    }

    const content = (
      <div className="error-display__content">
        <div className="error-display__icon">
          <AlertTriangle size={iconSize} />
        </div>

        <div className="error-display__text">
          {title && <h4 className="error-display__title">{title}</h4>}
          <p className="error-display__message">{errorMessage}</p>

          {showDetails && errorObj.stack && (
            <details className="error-display__details">
              <summary>Technical Details</summary>
              <pre className="error-display__stack">{errorObj.stack}</pre>
            </details>
          )}
        </div>

        <div className="error-display__actions">
          {onRetry && (
            <button className="btn btn--outline btn--sm" onClick={onRetry} title="Try again">
              <RefreshCw size={14} />
              Retry
            </button>
          )}

          {onDismiss && (
            <button className="btn btn--ghost btn--sm" onClick={onDismiss} title="Dismiss">
              <X size={14} />
              Dismiss
            </button>
          )}
        </div>
      </div>
    )

    if (inline) {
      return <div className={containerClasses}>{content}</div>
    }

    return <div className={containerClasses}>{content}</div>
  }
)

ErrorDisplay.displayName = 'ErrorDisplay'

/**
 * Compact error display for inline use (like in forms)
 */
export const InlineError: React.FC<{
  error?: string | Error | null
  className?: string
}> = ({ error, className = '' }) => {
  if (!error) return null

  const message = typeof error === 'string' ? error : error.message

  return (
    <div className={`inline-error ${className}`}>
      <AlertTriangle size={14} />
      <span>{message}</span>
    </div>
  )
}

/**
 * Error boundary fallback component
 */
export const ErrorFallback: React.FC<{
  error: Error
  resetError: () => void
  showDetails?: boolean
}> = ({ error, resetError, showDetails = false }) => (
  <div className="error-fallback">
    <ErrorDisplay
      error={error}
      title="Something went wrong"
      showDetails={showDetails}
      onRetry={resetError}
      size="lg"
    />
  </div>
)

/**
 * Network error display with specific messaging
 */
export const NetworkError: React.FC<{
  onRetry?: () => void
  message?: string
}> = ({ onRetry, message = 'Unable to connect. Please check your internet connection.' }) => (
  <ErrorDisplay error={new Error(message)} title="Connection Error" onRetry={onRetry} />
)

/**
 * Permission error display
 */
export const PermissionError: React.FC<{
  resource?: string
  action?: string
}> = ({ resource = 'resource', action = 'access' }) => (
  <ErrorDisplay
    error={new Error(`You don't have permission to ${action} this ${resource}.`)}
    title="Access Denied"
  />
)
